import React, { useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  RiComputerLine,
  RiInformationLine,
  RiShoppingCartLine,
  RiHistoryLine,
  RiDeleteBinLine,
  RiLockLine,
  RiToolsLine,
  RiSimCard2Line,
  RiFileTextLine,
  RiBrushLine,
  RiErrorWarningLine,
  RiStackLine,
} from 'react-icons/ri';
import deviceService from '../../services/deviceService';
import deviceMaintenanceService, { DeviceMaintenance } from '../../services/deviceMaintenanceService';
import supplierRepairService from '../../services/supplierRepairService';
import type { SupplierRepair } from '../../types/supplier-repair';
import { getStatusLabel as getSupplierRepairStatusLabel } from '../../types/supplier-repair';
import { useDeleteDevice } from '../../hooks/mutations/useDeviceMutations';
import { usePermission } from '../../hooks/usePermission';
import { queryKeys } from '../../queryClient';
import { Device, DeviceStatus } from '../../types/device';
import { DEVICES_PATH, deviceEditUrl } from '../../utils/deviceLinks';
import { parseRouteId } from '../../utils/routeId';
import { showAlert, showConfirm } from '../../utils/alert';
import { formatDate, formatDateTimeFull } from '../../utils/dateUtils';
import { deviceDeleteConfirmText, deviceKindName, deviceModelName } from '../../utils/deviceOptionLabel';
import { DeviceStatusBadge, MaintenanceStatusBadge } from '../../components/ui/StatusBadge';
import DeviceReplacementHistoryCard from '../../components/device/DeviceReplacementHistoryCard';
import DeviceRemoteAccessPanel from '../../components/remoteAccess/DeviceRemoteAccessPanel';
import DeviceStockMovementCard from '../../components/stock/DeviceStockMovementCard';
import {
  ItemRentalHistoryCard,
  ItemReturnItemsCard,
  ItemStockIssuesCard,
  currentRentalLabel,
  fetchItemRentalHistory,
} from '../../components/device/ItemRelatedCards';
import { ItemRentalHistory, itemRentalHistoryKey } from '../../services/itemRentalHistoryService';
import { getRentalStatusDisplay } from '../../utils/rentalStatusLabel';
import {
  DEVICE_CONDITION_LABELS,
  DEVICE_FIELD_LABELS,
  DEVICE_VALUE_FORMATTERS,
} from '../../components/device/deviceForm';
import {
  DetailPageLayout,
  DetailSection,
  InfoField,
  InfoGrid,
  RecordMetaCard,
  EntityActivityTimeline,
  RelatedRecordsCard,
  useDetailNavigation,
  detailNavState,
  headerButtonClass,
  headerButtonVariants,
} from '../../components/detail';
import { cn } from '../../utils/cn';

const RELATED_LIMIT = 10;

const money = (n: unknown) => {
  if (n === null || n === undefined || n === '') return null;
  const v = Number(n);
  return Number.isFinite(v) ? `${v.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} บาท` : null;
};

/**
 * หน้ารายละเอียดอุปกรณ์ — `/master-data/devices/:id` (แทนโมดัล "รายละเอียดอุปกรณ์" · มติ PO 28 ต.ค. 2026)
 *
 * ครบทุกช่องของฟอร์มเดิม (อ่านอย่างเดียว) + ทุกการ์ดของโมดัลเดิม (ซิม/สัญญา/Clear Cache/AnyDesk/
 * ประวัติเปลี่ยนอุปกรณ์/ความเคลื่อนไหวสต็อก/ข้อมูลการสร้าง) + การ์ดใหม่ (การถือครองตอนนี้ · สัญญาเช่าที่เคยอยู่ ·
 * เคสซ่อม · ส่งซ่อมผู้จำหน่าย · ใบตามเก็บ · ใบเบิก-ยืม · ประวัติกิจกรรม)
 *
 * กติกาที่หน้านี้ต้องรักษา (CLAUDE.md):
 * - ของนับจำนวน (is_serialized=false) **ไม่มีสถานะ** — ไม่แสดงป้ายสถานะ/AnyDesk/S/N · แสดงยอดคงเหลือแทน (กติกา 2)
 * - "ใครถือของอยู่" มาจาก backend (rental_hold · issued holder · rental-history.is_current) ไม่ derive จาก status (กติกา 4)
 * - ชื่ออุปกรณ์ = ประเภท + ยี่ห้อ + รุ่น ผ่าน deviceKindName (กติกา 13) · ไม่แสดง devices.anydesk_id (กติกา 10)
 */
const DeviceViewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const deviceId = parseRouteId(id);
  const location = useLocation();
  const { hasPermission } = usePermission();
  const { backTo, goTo, navState } = useDetailNavigation(DEVICES_PATH);
  const deleteDeviceMutation = useDeleteDevice();
  const [activityRefresh, setActivityRefresh] = useState(0);

  const { data: device, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.devices.detail(deviceId ?? 0),
    queryFn: () => deviceService.getDevice(deviceId!),
    enabled: deviceId !== null,
  });

  const hereState = detailNavState(location);

  // สัญญาที่ถือเครื่องอยู่ตอนนี้ — backend ตัดสินด้วย find_rental_holder (ไม่ใช่ status) · queryKey เดียวกับการ์ดประวัติ
  const canSeeRentals = hasPermission('rentals.view');
  const { data: rentalHistory } = useQuery({
    queryKey: itemRentalHistoryKey('device', deviceId ?? 0),
    queryFn: () => fetchItemRentalHistory('device', deviceId!),
    enabled: deviceId !== null && !!device && canSeeRentals,
    staleTime: 30 * 1000,
  });

  const handleRefresh = () => {
    refetch();
    setActivityRefresh((n) => n + 1);
  };

  const handleDelete = () => {
    if (!device) return;
    showConfirm(
      'ยืนยันการลบอุปกรณ์',
      deviceDeleteConfirmText(device),
      async () => {
        try {
          await deleteDeviceMutation.mutateAsync(device.id);
          goTo(backTo, { replace: true });
        } catch (e) {
          // useMutationWithRefresh แสดงข้อความจาก server แล้ว (เช่น อยู่ในสัญญา/ใบส่งซ่อม/ใบเบิก)
          console.error('Error deleting device:', e);
        }
      },
      { isDanger: true, confirmText: 'ลบ', cancelText: 'ยกเลิก' }
    );
  };

  const notFound = deviceId === null ? { response: { status: 404 } } : null;
  const serialized = device ? device.is_serialized !== false : true;
  const kind = device ? deviceKindName(device) : '';
  const title = device ? (kind || device.code || `อุปกรณ์ #${device.id}`) : 'รายละเอียดอุปกรณ์';

  return (
    <DetailPageLayout
      breadcrumbs={[{ label: 'อุปกรณ์', path: backTo }, { label: device?.code || 'รายละเอียด' }]}
      icon={<RiComputerLine />}
      title={title}
      recordId={device?.code}
      subtitle={device ? (serialized ? (device.serial_number ? `S/N ${device.serial_number}` : 'ติดตาม Serial Number') : 'อุปกรณ์แบบนับจำนวน') : undefined}
      backTo={backTo}
      onRefresh={handleRefresh}
      editTo={device ? deviceEditUrl(device.id) : undefined}
      editPermission="devices.edit"
      editState={navState}
      actions={
        <>
          {/* ลิงก์ข้ามโมดูลต้องมีสิทธิ์ปลายทาง (G-12) · route /maintenance/create/:device_id preselect เครื่องนี้ */}
          {device && serialized && hasPermission('maintenance.create') && (
            <Link to={`/maintenance/create/${device.id}`} className={cn(headerButtonClass, headerButtonVariants.neutral)}>
              <RiToolsLine className="h-4 w-4 mr-1.5" />
              แจ้งซ่อม
            </Link>
          )}
          {device && hasPermission('devices.delete') && (
            <button type="button" onClick={handleDelete} className={cn(headerButtonClass, headerButtonVariants.danger)}>
              <RiDeleteBinLine className="h-4 w-4 mr-1.5" />
              ลบ
            </button>
          )}
        </>
      }
      loading={deviceId !== null && isLoading}
      error={notFound || error}
      notFoundMessage="ไม่พบอุปกรณ์ที่ระบุ (อาจถูกลบไปแล้ว)"
      onRetry={() => refetch()}
      sidebar={
        device && (
          <>
            <RecordMetaCard
              createdAt={device.created_at}
              createdBy={device.created_by}
              updatedAt={device.updated_at}
              updatedBy={device.updated_by}
            />
            <DetailSection title="ประวัติกิจกรรม" icon={<RiHistoryLine />}>
              <EntityActivityTimeline
                entityType="device"
                entityId={device.id}
                refreshTrigger={activityRefresh}
                fieldLabels={DEVICE_FIELD_LABELS}
                valueFormatters={DEVICE_VALUE_FORMATTERS}
              />
            </DetailSection>
          </>
        )
      }
    >
      {device && (
        <>
          <DetailSection
            title="ข้อมูลพื้นฐาน"
            icon={<RiInformationLine />}
            actions={
              serialized ? (
                <span data-testid="device-status-badge">
                  <DeviceStatusBadge status={String(device.status).toUpperCase() as DeviceStatus} size="sm" bordered />
                </span>
              ) : (
                // PO 2026-09-08: ของนับจำนวนไม่มีสถานะ — ไม่โชว์ป้าย (1 แถว = ของหลายชิ้น ป้ายเดียวคือการโกหก)
                <span
                  className="text-xs text-secondary-500"
                  title="อุปกรณ์แบบนับจำนวนไม่มีสถานะ — ความถูกต้องอยู่ที่ยอดคงเหลืออย่างเดียว"
                  data-testid="device-no-status"
                >
                  นับจำนวน · ไม่มีสถานะ
                </span>
              )
            }
          >
            <InfoGrid>
              <InfoField label="รหัสอุปกรณ์" value={device.code} mono />
              <InfoField label="ประเภทอุปกรณ์" value={device.device_type?.name} />
              <InfoField label="ยี่ห้อ" value={device.brand?.name} />
              <InfoField label="รุ่น" value={device.device_model?.name || device.model || null} />
              <InfoField label="ประเภทการจัดการ" value={serialized ? 'ติดตาม Serial Number (รายเครื่อง)' : 'นับจำนวนคงคลัง (ไม่ระบุ S/N)'} />
              {serialized ? (
                <>
                  <InfoField label="Serial Number" value={device.serial_number} mono />
                  <InfoField label="IMEI" value={device.imei} mono />
                </>
              ) : (
                <>
                  <InfoField label="จำนวนคงเหลือ" testId="device-quantity">
                    <span className="font-semibold">{(device.quantity ?? 0).toLocaleString('th-TH')} {device.unit}</span>
                    {device.quantity <= device.minimum_quantity && (
                      <span className="ml-2 inline-flex px-2 py-0.5 rounded-full text-xs bg-danger-100 text-danger-700" data-testid="device-low-stock">
                        ถึง/ต่ำกว่าขั้นต่ำ
                      </span>
                    )}
                  </InfoField>
                  <InfoField label="จำนวนขั้นต่ำ" value={`${device.minimum_quantity ?? 0} ${device.unit || ''}`} />
                  <InfoField label="หน่วยนับ" value={device.unit} />
                </>
              )}
              <InfoField label="วันที่ซื้อ" value={device.purchase_date ? formatDate(device.purchase_date) : null} />
              <InfoField label="ตำแหน่งที่ตั้ง/สถานที่" value={device.location} />
              <InfoField label="ลำดับเครื่อง" value={device.device_number ?? null} />
              <InfoField label="หมายเหตุตำแหน่ง" value={device.location_note} />
              <InfoField label="หมายเหตุ" value={device.notes} multiline fullWidth />
            </InfoGrid>
          </DetailSection>

          <DeviceHoldingSection
            device={device}
            serialized={serialized}
            canSeeRentals={canSeeRentals}
            rentalHistory={rentalHistory}
          />

          <DetailSection title="ข้อมูลการจัดซื้อ" icon={<RiShoppingCartLine />}>
            <InfoGrid>
              <InfoField label="ผู้จำหน่าย" value={device.supplier ? [device.supplier.code, device.supplier.name].filter(Boolean).join(' ') : null} />
              {/* `??` ไม่ใช่ `||` — ราคาซื้อ 0 บาทเป็นค่าจริง (ของแถม) */}
              <InfoField label="ราคาซื้อ" value={money(device.purchase_price)} />
              <InfoField label="เลขที่ใบกำกับภาษี" value={device.invoice_number} mono />
              <InfoField label="วันที่รับเข้าคลัง" value={device.receive_date ? formatDate(device.receive_date) : null} />
              <InfoField label="สภาพสินค้า" value={device.condition ? DEVICE_CONDITION_LABELS[device.condition] ?? device.condition : null} />
              <InfoField label="มูลค่าทางบัญชี" value={money(device.book_value)} />
              <InfoField label="เลขครุภัณฑ์" value={device.asset_number} mono />
            </InfoGrid>
          </DetailSection>

          {/* การ์ดเดิมของโมดัล — แสดงเมื่อ backend ส่งข้อมูลมา */}
          {device.sim && (
            <DetailSection title="ข้อมูลซิมการ์ด" icon={<RiSimCard2Line />}>
              <InfoGrid>
                <InfoField label="ค่าย" value={device.sim.operator} />
                <InfoField label="เบอร์โทร" value={device.sim.phone_number} mono />
                <InfoField label="วันหมดอายุ" value={device.sim.expiry_date ? formatDateTimeFull(device.sim.expiry_date) : null} />
                <InfoField label="ลงทะเบียนโดย" value={device.sim.registration_name} />
              </InfoGrid>
            </DetailSection>
          )}

          {device.contract && (
            <DetailSection title="ข้อมูลสัญญา" icon={<RiFileTextLine />} testId="device-contract-card">
              <InfoGrid>
                <InfoField label="บริษัท" value={device.contract.company_name} />
                <InfoField label="โครงการ" value={device.contract.site_name} />
                <InfoField label="วันเริ่มสัญญา" value={device.contract.start_date ? formatDateTimeFull(device.contract.start_date) : null} />
                <InfoField label="วันสิ้นสุดสัญญา" value={device.contract.end_date ? formatDateTimeFull(device.contract.end_date) : null} />
                <InfoField label="พนักงานขาย" value={device.contract.sales_person} />
                <InfoField label="วันส่งมอบ" value={device.contract.delivery_date ? formatDateTimeFull(device.contract.delivery_date) : null} />
              </InfoGrid>
            </DetailSection>
          )}

          {device.clear_cache && device.clear_cache.last_clear_date && (
            <DetailSection title="ข้อมูล Clear Cache" icon={<RiBrushLine />}>
              <InfoGrid>
                <InfoField label="วันที่ล้างแคชล่าสุด" value={formatDateTimeFull(device.clear_cache.last_clear_date)} />
                <InfoField label="ล้างโดย" value={device.clear_cache.cleared_by} />
              </InfoGrid>
            </DetailSection>
          )}

          {/* AnyDesk ปัจจุบัน (ของรายการเช่าที่เครื่องอยู่) — ค่าเดียวกับหน้าการเช่า/เคสซ่อม (23 ก.ย. 2026)
              ของนับจำนวนไม่มี AnyDesk ต่อเครื่อง จึงไม่แสดง */}
          {serialized && <DeviceRemoteAccessPanel deviceId={device.id} />}

          <ItemRentalHistoryCard kind="device" itemId={device.id} rowState={hereState} counted={!serialized} />

          {serialized && (
            <RelatedRecordsCard<DeviceMaintenance>
              title="เคสซ่อมบำรุง"
              icon={<RiToolsLine />}
              permission="maintenance.view"
              queryKey={['item-related', 'device', device.id, 'maintenance']}
              fetcher={() => deviceMaintenanceService.getMaintenanceRecords({ device_id: device.id, limit: RELATED_LIMIT })}
              rowKey={(m) => m.id}
              rowTo={(m) => `/maintenance/view/${m.id}`}
              rowState={hereState}
              emptyText="เครื่องนี้ยังไม่มีเคสซ่อม"
              testId="device-related-maintenance"
              renderRow={(m) => (
                <>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-secondary-900">
                      <span className="font-mono">#{m.id}</span> {m.maintenance_type?.name || ''}
                    </p>
                    <p className="text-xs text-secondary-500 [overflow-wrap:anywhere]">
                      {formatDate(m.maintenance_date)}
                      {m.site?.name ? ` · ${m.site.name}` : ''}
                    </p>
                  </div>
                  {m.status && <MaintenanceStatusBadge status={m.status as any} size="sm" />}
                </>
              )}
            />
          )}

          {serialized && (
            <RelatedRecordsCard<SupplierRepair>
              title="ส่งซ่อมผู้จำหน่าย"
              icon={<RiStackLine />}
              permission="supplier_repairs.view"
              queryKey={['item-related', 'device', device.id, 'supplier-repairs']}
              fetcher={() => supplierRepairService.getSupplierRepairs({ device_id: device.id, limit: RELATED_LIMIT })}
              rowKey={(r) => r.id}
              viewAllTo="/supplier-repairs"
              emptyText="เครื่องนี้ยังไม่เคยส่งซ่อมผู้จำหน่าย"
              testId="device-related-supplier-repairs"
              renderRow={(r) => (
                <>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-secondary-900 font-mono">{r.request_number}</p>
                    <p className="text-xs text-secondary-500 [overflow-wrap:anywhere]">
                      {r.supplier?.name || '-'}
                      {r.sent_date ? ` · ส่ง ${formatDate(r.sent_date)}` : ''}
                    </p>
                  </div>
                  <span className="text-xs text-secondary-700 flex-shrink-0">{getSupplierRepairStatusLabel(r.status)}</span>
                </>
              )}
            />
          )}

          <ItemReturnItemsCard kind="device" itemId={device.id} />
          <ItemStockIssuesCard kind="device" itemId={device.id} rowState={hereState} />

          {/* ประวัติการเปลี่ยนอุปกรณ์ของเครื่องนี้ (ซ่อนเองถ้าไม่มีสิทธิ์ device_replacements.view) */}
          <DeviceReplacementHistoryCard deviceId={device.id} deviceCode={device.code} deviceSerial={device.serial_number} />

          {/* ความเคลื่อนไหวสต็อกของเครื่องนี้ (ซ่อนเองถ้าไม่มีสิทธิ์ stock_movements.view) */}
          <DeviceStockMovementCard deviceId={device.id} />
        </>
      )}
    </DetailPageLayout>
  );
};

/**
 * "การถือครองตอนนี้" — ข้อเท็จจริงจาก backend เท่านั้น (ห้าม derive จาก status · CLAUDE.md กติกา 4)
 * - rental_hold: สัญญาที่ยังไม่จบผูกเครื่องอยู่ (ข้อความ + ทางออกมาจาก backend)
 * - has_pending_return: ใบตามเก็บยังเปิดค้าง (ของอาจยังอยู่กับลูกค้า · ห้ามหยิบ L2)
 * - ISSUED: ผู้ยืม + เลขใบเบิก · RESERVED: กันไว้ให้ใบงานเตรียมเครื่อง
 * ของนับจำนวนไม่มี ISSUED/RESERVED รายเครื่อง แต่ใบตามเก็บ/สัญญาผูกได้ จึงยังแสดงสองข้อแรก
 */
const DeviceHoldingSection: React.FC<{
  device: Device;
  serialized: boolean;
  canSeeRentals: boolean;
  rentalHistory?: ItemRentalHistory;
}> = ({ device, serialized, canSeeRentals, rentalHistory }) => {
  const status = String(device.status || '').toUpperCase();
  const hold = device.rental_hold;
  const pending = device.has_pending_return;
  const issued = serialized && status === DeviceStatus.ISSUED;
  const reserved = serialized && status === DeviceStatus.RESERVED;
  // rental_hold มีเฉพาะเครื่องที่ "ขึ้นว่าง" แต่สัญญายังถือ — เครื่องที่ RENTED ตามปกติต้องดูจาก rental-history
  const currentRows = (rentalHistory?.items ?? []).filter((r) => r.is_current && r.rental_id !== hold?.rental_id);
  const nothing = !hold && !pending && !issued && !reserved && currentRows.length === 0;
  return (
    <DetailSection title="การถือครองตอนนี้" icon={<RiLockLine />} testId="device-holding">
      <div className="space-y-3 text-sm">
        {currentRows.map((r) => {
          const st = getRentalStatusDisplay(r.status);
          return (
            <div key={r.rental_item_id} className="p-3 rounded-lg border border-primary-200 bg-primary-50 text-primary-900" data-testid="device-current-rental">
              <span className="font-semibold">{currentRentalLabel(r, rentalHistory?.counted)}:</span>{' '}
              <Link to={`/rentals/view/${r.rental_id}`} className="font-mono underline">{r.rental_number || `#${r.rental_id}`}</Link>
              <span className={cn('ml-2 px-2 py-0.5 rounded-full text-xs', st.badgeColor)}>{st.label}</span>
              <p className="mt-1 text-xs [overflow-wrap:anywhere]">{[r.company_name, r.site_name].filter(Boolean).join(' · ') || '-'}</p>
            </div>
          );
        })}
        {hold && (
          <div className="flex items-start gap-2 p-3 rounded-lg border border-info-200 bg-info-50 text-info-800" data-testid="device-rental-hold">
            <RiLockLine className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div className="min-w-0">
              <Link to={`/rentals/view/${hold.rental_id}`} className="font-semibold underline">
                🔒 ผูกกับสัญญา {hold.rental_number || `#${hold.rental_id}`}
              </Link>
              {hold.goods_returned ? ' · รับคืนแล้ว' : ''}
              <p className="mt-1 [overflow-wrap:anywhere]">{hold.message}</p>
            </div>
          </div>
        )}
        {pending && (
          <div className="flex items-start gap-2 p-3 rounded-lg border border-warning-200 bg-warning-50 text-warning-800" data-testid="device-pending-return">
            <RiErrorWarningLine className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>
              ⚠ รอรับคืน{(device.pending_return_count || 0) > 1 ? ` (${device.pending_return_count})` : ''} — ยังมีรายการตามเก็บที่ยังไม่ปิด
              ของอาจยังอยู่กับลูกค้า · ระบบจะไม่ให้นำ{serialized ? 'เครื่อง' : 'ของ'}นี้ไปทำสัญญาใหม่จนกว่าจะบันทึกรับคืน หรือยกเลิกรายการตามเก็บ
            </span>
          </div>
        )}
        {issued && (
          <div className="p-3 rounded-lg border border-purple-200 bg-purple-50 text-purple-800" data-testid="device-issued-holder">
            เบิกไปใช้ภายใน — ผู้ยืม: <span className="font-semibold">{device.issued_to_display || '-'}</span>
            {device.current_stock_issue_number && (
              <>
                {' · ใบเบิก '}
                {device.current_stock_issue_id ? (
                  <Link to={`/stock/pending-returns?issue=${device.current_stock_issue_id}`} className="font-mono underline">
                    {device.current_stock_issue_number}
                  </Link>
                ) : (
                  <span className="font-mono">{device.current_stock_issue_number}</span>
                )}
              </>
            )}
          </div>
        )}
        {reserved && (
          <div className="p-3 rounded-lg border border-accent-200 bg-accent-50 text-accent-800" data-testid="device-reserved">
            จองไว้ให้ใบงานเตรียมเครื่อง — ปล่อยของได้ที่เมนู &quot;การเช่า › ใบงานเตรียมเครื่อง&quot;
          </div>
        )}
        {nothing && canSeeRentals && (
          <p className="text-secondary-600" data-testid="device-holding-none">
            {serialized ? 'ไม่มีสัญญา ใบเบิก หรือใบงานถือเครื่องนี้อยู่' : 'ไม่มีสัญญาหรือใบตามเก็บผูกอยู่ — ยอดคงเหลือคือของในคลัง'}
          </p>
        )}
        {/* ไม่มีสิทธิ์ดูสัญญา = ไม่รู้ว่าสัญญาไหนถืออยู่ → ห้ามบอกว่า "ไม่มี" (จอห้ามพูดเกินที่รู้) */}
        {nothing && !canSeeRentals && (
          <p className="text-secondary-500" data-testid="device-holding-unknown">
            ไม่มีใบตามเก็บหรือใบเบิกค้าง · ข้อมูลสัญญาเช่าต้องใช้สิทธิ์ดูสัญญาเช่า
          </p>
        )}
      </div>
    </DetailSection>
  );
};

export default DeviceViewPage;
